import dayjs from 'dayjs';

import {
  calculateCarbonIntensityScore,
  calculateGreenhouseGasReduction,
  calculateSustainabilityTier,
  calculateTonnesAbated,
  calculateUsabilityTier,
} from '../calculations/calculations.js';
import {
  CertificationSchemeToLabel,
  conversionProcessToLabel,
  FeedstockNameToLabel,
  JetFuelBaselineCarbonIntensityToLabel,
} from '../calculations/constants.js';
import type { ClaimMetadata } from '../claim-meta.js';
import type { AdditionalityApproachType } from '../unit-meta/index.js';
import {
  isCorsiaActual,
  isCorsiaDefault,
  isEuActual,
  isEuDefault,
  type IssueMetadata,
  type SustainabilityTier,
} from '../unit-meta/index.js';
import type {
  UnitBlockType,
  UnitStatus,
} from './backend-types.js';
import type {
  UnitType,
  UsabilityTier,
} from './types.js';

export const sustainabilityTier = (unit: { rootMetadata: IssueMetadata }): SustainabilityTier => calculateSustainabilityTier(unit.rootMetadata);

export const usabilityTier = (unit: { rootMetadata: IssueMetadata; isParentTreeClaimed: boolean; hasLinkedTree: boolean; hasParentTree: boolean }): UsabilityTier => {
  const { rootMetadata, hasLinkedTree, isParentTreeClaimed } = unit;
  return calculateUsabilityTier(rootMetadata, { unitType: unitType(unit), hasLinkedTree, isParentTreeClaimed, sustainabilityTier: sustainabilityTier({ rootMetadata }) });
};

export const rootUsabilityTier = (unit: { rootMetadata: IssueMetadata }) => usabilityTier({
  rootMetadata: unit.rootMetadata,
  hasLinkedTree: false,
  hasParentTree: false,
  isParentTreeClaimed: false,
});

export const carbonIntensityScore = (unit: { rootMetadata: IssueMetadata }) => {
  return calculateCarbonIntensityScore(unit.rootMetadata, { sustainabilityTier: sustainabilityTier(unit) });
};

export const unitType = ({ hasParentTree }: { hasParentTree: boolean }): UnitType => {
  if (hasParentTree) {
    return 'SAFcE';
  }
  return 'SAFcA';
};

export const assuranceLevel = ({ rootMetadata: { assuranceLevel } }: { rootMetadata: IssueMetadata }): IssueMetadata['assuranceLevel'] => {
  return assuranceLevel;
};

export const lcaType = ({ rootMetadata }: { rootMetadata: IssueMetadata }) => {
  if (isCorsiaDefault(rootMetadata)) {
    return 'CORSIA_DEFAULT';
  }
  if (isCorsiaActual(rootMetadata)) {
    return 'CORSIA_ACTUAL';
  }

  if (isEuActual(rootMetadata)) {
    return 'EU_ACTUAL';
  }

  if (isEuDefault(rootMetadata)) {
    return 'EU_DEFAULT';
  }

  throw new Error('LCA not supported');
};

export const typeCode = (unit: { rootMetadata: IssueMetadata; isParentTreeClaimed: boolean; hasLinkedTree: boolean; hasParentTree: boolean }) => {
  const resolvedUsabilityTier = usabilityTier(unit);
  const resolvedUnitType = unitType(unit);
  const typeCodeUnitType = (resolvedUnitType === 'SAFcA' && resolvedUsabilityTier === 2) ? 'SAFc' : resolvedUnitType;
  return `${typeCodeUnitType}-${sustainabilityTier(unit)}${resolvedUsabilityTier}-${assuranceLevel(unit)}`;
};

export const expirationDate = ({ rootCreatedAt }: { rootCreatedAt: string }) => {
  if (!rootCreatedAt) {
    return null;
  }
  const date = dayjs(new Date(rootCreatedAt)).add(2, 'year');
  if (!dayjs(date).isValid()) {
    return null;
  }

  return date.toISOString();
};

export const issuanceDate = ({ rootCreatedAt }: { rootCreatedAt: string }) => {
  if (rootCreatedAt) {
    return rootCreatedAt;
  }
  return null;
};

export const co2Abated = (unit: { rootMetadata: IssueMetadata; volume: number }) => calculateTonnesAbated(unit.rootMetadata, unit.volume, baseline(unit), carbonIntensityScore(unit));
export const freeCo2Abated = (unit: { rootMetadata: IssueMetadata; volume: number; freeVolume: number }) => ((co2Abated(unit) * unit.freeVolume / unit.volume));

export const country = ({ rootMetadata: { batch: { upliftCountry } } }: { rootMetadata: IssueMetadata }) => {
  return upliftCountry?.code ?? 'US';
};

export const status = ({ status }: { status: UnitStatus }) => {
  return status;
};

export const historyStatus = ({ status, type }: { status: UnitStatus; type: UnitBlockType }) => {
  if (type === 'Claim' && status === 'active') {
    return 'retired';
  }

  if (type === 'Issue' && status === 'active') {
    return 'issued';
  }

  return status;
};

export const blockId = ({ id }: { id: string }) => {
  return id;
};

export const additionalCertificationScheme = ({ rootMetadata }: { rootMetadata: IssueMetadata }) => {
  const additional = rootMetadata['certification']['additionalScheme'];

  if (!additional) {
    return '';
  }
  if (additional === 'other') {
    return additional;
  }

  return CertificationSchemeToLabel[
    additional
  ];
};

export const jetFuelBaselineCarbonIntensity = ({ rootMetadata }: { rootMetadata: IssueMetadata }) => {
  return JetFuelBaselineCarbonIntensityToLabel[rootMetadata['product'].jetFuelBaselineCarbonIntensity] ?? '';
};

export const baseline = ({ rootMetadata }: { rootMetadata: IssueMetadata }) => {
  return Number(rootMetadata['product'].jetFuelBaselineCarbonIntensity.replace('g', ''));
};

export const conversionProcess = ({ rootMetadata }: { rootMetadata: IssueMetadata }) => {
  return conversionProcessToLabel[rootMetadata['product']['conversionProcess']] ?? '';
};

export const unitOwner = ({ ownerId }: { ownerId: string }) => {
  return ownerId;
};

export const fuelProducingCompany = ({ rootMetadata: { batch: { companyName } } }: { rootMetadata: IssueMetadata }) => {
  return companyName;
};

export const feedstockProductionCountryCode = ({ rootMetadata: { product: { countries } } }: { rootMetadata: IssueMetadata }) => {
  return countries[0].code;
};

export const feedstockProductionCountryName = ({ rootMetadata: { product: { countries } } }: { rootMetadata: IssueMetadata }) => {
  return countries[0].name;
};

export const totalLCAValue = ({ rootMetadata: { greenhouseGas } }: { rootMetadata: IssueMetadata }) => {
  if ('totalLCAValueCorsiaDefault' in greenhouseGas) {
    return greenhouseGas.totalLCAValueCorsiaDefault;
  }
  if ('totalLCAValueCorsiaActual' in greenhouseGas) {
    return greenhouseGas.totalLCAValueCorsiaActual;
  }
  if ('totalLCAValueEuDefault' in greenhouseGas) {
    return greenhouseGas.totalLCAValueEuDefault;
  }
  if ('totalLCAValueEuActual' in greenhouseGas) {
    return greenhouseGas.totalLCAValueEuActual;
  }
  return 0;
};

export const certificationScheme = ({ rootMetadata: { certification: { scheme } } }: { rootMetadata: IssueMetadata }) => {
  return CertificationSchemeToLabel[scheme];
};

export const productionFeedstock = ({ rootMetadata: { product: { feedStock } } }: { rootMetadata: IssueMetadata }) => {
  return feedStock && 'name' in feedStock ? FeedstockNameToLabel[feedStock.name!] : null;
};

export const productionYear = ({ rootMetadata: { batch: { productionYear } } }: { rootMetadata: IssueMetadata }) => {
  return productionYear.toString();
};

export const retirementYear = ({ metadata }: { metadata?: ClaimMetadata }) => {
  return metadata ? metadata['retirementYear'] : null;
};

export const retirementCountry = ({ metadata }: { metadata?: ClaimMetadata }) => {
  return metadata ? metadata['retirementCountry'] : null;
};

export const parentId = ({ rootId }: { rootId: string }) => {
  return rootId;
};

export const createdAt = ({ createdAt }: { createdAt?: string }) => {
  if (createdAt) {
    return new Date(createdAt).toISOString();
  }
  return null;
};

export const countryOfProductionCode = ({ rootMetadata: { supplier: { siteAddress: { country: { code } } } } }: { rootMetadata: IssueMetadata }) => {
  return code;
};

export const countryOfProductionName = ({ rootMetadata: { supplier: { siteAddress: { country: { name } } } } }: { rootMetadata: IssueMetadata }) => {
  return name;
};

export const claimPurpose = ({ metadata }: { metadata?: ClaimMetadata }) => {
  return metadata?.claimPurpose ?? null;
};

// Older units might have this field missing
export const additionalityApproachType = ({ rootMetadata }: { rootMetadata: IssueMetadata }): AdditionalityApproachType | null => {
  return rootMetadata.batch.additionalityApproach?.type ?? null;
};

export const ghgReduction = (unit: { rootMetadata: IssueMetadata }) => {
  const jetFuelIntensity = jetFuelBaselineCarbonIntensity(unit);
  const carbonIntensity = carbonIntensityScore(unit);

  if (!jetFuelIntensity) {
    return 0;
  }

  return calculateGreenhouseGasReduction(Number(jetFuelIntensity.split('g ')[0]), carbonIntensity);
};

export const customFieldsParser = {
  sustainabilityTier,
  usabilityTier,
  rootUsabilityTier,
  additionalityApproachType,
  carbonIntensityScore,
  unitType,
  assuranceLevel,
  lcaType,
  typeCode,
  expirationDate,
  issuanceDate,
  co2Abated,
  country,
  status,
  historyStatus,
  blockId,
  additionalCertificationScheme,
  jetFuelBaselineCarbonIntensity,
  baseline,
  conversionProcess,
  unitOwner,
  fuelProducingCompany,
  feedstockProductionCountryCode,
  feedstockProductionCountryName,
  totalLCAValue,
  certificationScheme,
  productionFeedstock,
  productionYear,
  retirementYear,
  parentId,
  createdAt,
  countryOfProductionCode,
  countryOfProductionName,
  claimPurpose,
  ghgReduction,
  freeCo2Abated,
};

export type ComputedFields = {
  [prop in keyof typeof customFieldsParser]: ReturnType<typeof customFieldsParser[prop]>
};

export type ExtraParsedFields = {
  rootOwnerName: string;
  rootOwnerId: string;
  ownerName: string;
  ownerCompanyName: string;
  historyOwnerTypes: string;
  historyCompanyCount: number;
  genesisBlockForSAFcE?: string;
  verifiedComputeUrl: string | null;
  SAFcAClaimBeneficiaryCompanyName?: string;
  SAFcEClaimBeneficiaryCompanyName?: string;
  logisticsProviderBeneficiaryCompanyName?: string;
};
